/**
 * 强制更新全屏蒙层组件
 * 在 App.tsx 根级别渲染，确保登录页和主布局都能展示强制更新蒙层。
 * 强制更新时不走常规弹窗流程（updateModalOpen=false），统一由此蒙层处理全流程：
 *   available → 展示更新信息 + "立即更新"按钮
 *   downloading → 进度条
 *   downloaded → "立即更新"按钮（安装）
 *   installing → 提示安装中
 *   error → "重新下载"按钮
 */

import React from 'react';
import { Button, Modal, Progress, message } from 'antd';
import { useAppUpdateStore } from '../../../store/appUpdate';
import { useAppUpdateActions } from '../../../hooks/useAppUpdateActions';
import appDesktopIcon from '../../../../../build/icon.png';
import './ForceUpdateOverlay.css';

export const ForceUpdateOverlay: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { handleUpdatePrimaryAction } = useAppUpdateActions(messageApi);

  const updateCheckAuthority = useAppUpdateStore((s) => s.updateCheckAuthority);
  const forceUpdateFlowActive = useAppUpdateStore((s) => s.forceUpdateFlowActive);
  const updateActionSubmitting = useAppUpdateStore((s) => s.updateActionSubmitting);
  const forceUpdateFlowError = useAppUpdateStore((s) => s.forceUpdateFlowError);
  const pendingUpdateStatus = useAppUpdateStore((s) => s.pendingUpdateStatus);
  const downloadingUpdateStatus = useAppUpdateStore((s) => s.downloadingUpdateStatus);
  const installingUpdateStatus = useAppUpdateStore((s) => s.installingUpdateStatus);
  const availableUpdateStatus = useAppUpdateStore((s) => s.availableUpdateStatus);

  const hasServerConfirmedUpdate = updateCheckAuthority === 'has-update';
  // 强制更新时 updateModalOpen 为 false，蒙层始终可见
  const isVisible = hasServerConfirmedUpdate && forceUpdateFlowActive;

  // 状态优先级：installing > downloaded > downloading > available
  const overlayStatus = hasServerConfirmedUpdate
    ? (installingUpdateStatus ?? pendingUpdateStatus ?? downloadingUpdateStatus ?? availableUpdateStatus)
    : null;

  if (!isVisible || !overlayStatus) {
    return contextHolder;
  }

  const isAvailable = availableUpdateStatus?.status === 'available' && !downloadingUpdateStatus && !pendingUpdateStatus && !installingUpdateStatus;
  const isDownloading = Boolean(downloadingUpdateStatus);
  const isDownloaded = pendingUpdateStatus?.status === 'downloaded';
  const isInstalling = installingUpdateStatus?.status === 'installing';
  const canRetryDownload = Boolean(availableUpdateStatus) && !isDownloaded && !isInstalling && !isDownloading;

  const progressPercent = typeof downloadingUpdateStatus?.progressPercent === 'number'
    ? Math.max(0, Math.min(99, Math.floor(downloadingUpdateStatus.progressPercent)))
    : 0;

  // 根据当前状态决定标题和描述
  let title = '发现新版本';
  let description = '检测到强制更新版本，请立即完成更新后继续使用。';

  if (forceUpdateFlowError) {
    title = '更新下载失败';
    description = forceUpdateFlowError;
  } else if (isInstalling) {
    title = '正在启动更新';
    description = '安装程序已启动，请根据系统提示继续完成更新。';
  } else if (isDownloaded) {
    title = '更新包已准备完成';
    description = '检测到强制更新版本已下载完成，请立即完成更新安装。';
  } else if (isDownloading) {
    title = '正在下载更新';
    description = '为保证版本一致性，当前需先完成强制更新后再继续使用。';
  }

  // 更新说明
  const { releaseNotesList } = overlayStatus;
  const releaseNotes = overlayStatus.releaseNotes
    ?.split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

  const renderNotes = () => {
    // 多版本：按版本分组
    if (releaseNotesList && releaseNotesList.length > 0) {
      return (
        <div className="force-update-overlay__notes">
          {releaseNotesList.map((entry, idx) => (
            <div key={`${entry.version}-${entry.buildVersion}`} className="force-update-overlay__version-group">
              {(releaseNotesList.length > 1 || idx === 0) && (
                <p className="force-update-overlay__version-title">V{entry.version}</p>
              )}
              {entry.releaseNotes.split(/\r?\n/).map((item) => item.trim()).filter(Boolean).map((note, i) => (
                <p key={`${note}-${i}`} className="force-update-overlay__note">{note}</p>
              ))}
            </div>
          ))}
        </div>
      );
    }
    // 回退：单条
    if (!releaseNotes || releaseNotes.length === 0) return null;
    return (
      <div className="force-update-overlay__notes">
        {releaseNotes.map((note, index) => (
          <p key={`${note}-${index}`} className="force-update-overlay__note">{note}</p>
        ))}
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        open
        centered
        closable={false}
        keyboard={false}
        footer={null}
        width={420}
        destroyOnHidden={false}
        maskClosable={false}
        onCancel={() => undefined}
        className="force-update-overlay"
        styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.62)' } }}
      >
        <div className="force-update-overlay__card">
          <span className="force-update-overlay__icon" aria-hidden="true">
            <img src={appDesktopIcon} alt="" className="force-update-overlay__icon-image" />
          </span>
          <h3 className="force-update-overlay__title">{title}</h3>
          <p className="force-update-overlay__version">
            {overlayStatus.latestVersion ? `V${overlayStatus.latestVersion}` : '新版本已就绪'}
          </p>
          <p className="force-update-overlay__description">{description}</p>

          {/* available 状态：展示更新说明 */}
          {isAvailable ? renderNotes() : null}

          {/* downloading 状态：进度条 */}
          {isDownloading && !forceUpdateFlowError ? (
            <div className="force-update-overlay__progress-block">
              <Progress
                percent={progressPercent}
                showInfo={false}
                strokeColor="#4f39f6"
                trailColor="rgba(79, 57, 246, 0.12)"
              />
              <span className="force-update-overlay__progress-text">
                {`当前下载进度 ${progressPercent}%`}
              </span>
            </div>
          ) : null}

          <div className="force-update-overlay__actions">
            {/* available 状态：点击开始下载 */}
            {isAvailable && !forceUpdateFlowError ? (
              <Button
                type="primary"
                className="force-update-overlay__submit"
                loading={updateActionSubmitting}
                onClick={() => void handleUpdatePrimaryAction()}
              >
                立即更新
              </Button>
            ) : null}
            {/* downloaded 状态：点击安装 */}
            {isDownloaded ? (
              <Button
                type="primary"
                className="force-update-overlay__submit"
                loading={updateActionSubmitting}
                onClick={() => void handleUpdatePrimaryAction()}
              >
                立即更新
              </Button>
            ) : null}
            {/* error 状态：重新下载 */}
            {forceUpdateFlowError && canRetryDownload ? (
              <Button
                type="primary"
                className="force-update-overlay__submit"
                loading={updateActionSubmitting}
                onClick={() => void handleUpdatePrimaryAction()}
              >
                重新下载
              </Button>
            ) : null}
          </div>
        </div>
      </Modal>
    </>
  );
};
